import { Component, OnInit } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  tap,
  take,
  takeUntil,
  catchError,
  map,
} from 'rxjs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  IOptionOutput,
  ICtaOutput,
  ITableColumns,
  ITableConfigParams,
  ITableData,
  TableComponent,
  ITableConfigParamsFilter,
} from '../../../shared/components/table/table.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { ToastService } from '../../../core/services/utils/toast.service';
import { IonicColors } from '../../../shared/enums/ionicColors.enum';
import { IGetDevicesParams, SensorService } from '../../../core/services/sensors.service';
import { ISimpleInput } from '../../../shared/components/form/form.component';
import { DialogComponent, IDialogData } from '../../../shared/components/dialog/dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Validators } from '@angular/forms';
import moment from 'moment';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { IDevice } from '../../../shared/models/be/device';
import { IUserTableFilterPreference } from '../../../shared/models/be/userPreferences';
import { FiltersList } from '../../../shared/components/table/filtersList';
import { IonContent } from "@ionic/angular/standalone";
import { PageEvent } from '@angular/material/paginator';
import { TableService } from '../../../shared/components/table/table.service';

@Component({
  selector: 'devices',
  templateUrl: './devices.page.html',
  styleUrls: ['./devices.page.scss'],
  imports: [
    CommonModule,
    MatGridListModule,
    MatIconModule,
    TableComponent,
    TranslateModule,
    RouterModule,
    IonContent
  ]
})
export class DevicesPage implements OnInit {
  public tableColumns: ITableColumns[] = [
    { label: 'STATUS', id: 'status', showCol: true, cannotHide: true },
    { label: 'STEREO_STATUS', id: 'stereoCalibrationState', showCol: true },
    { label: 'SIGNAL', id: 'signal', showCol: true },
    { label: 'REF', id: 'label', showCol: true },
    { label: 'OS_NAME', id: 'os_name', showCol: true },
    { label: 'TEMPERATURE', id: 'temperature_celsius', showCol: true },
    { label: 'MENTORAGE_VERSION', id: 'mentorage_image_tag', showCol: true },
    { label: 'IDM_VERSION', id: 'release_channel', showCol: true },
    { label: 'LAST_HEARTBEAT', id: 'lastHeartbeat', showCol: true },
    { label: 'LINK_STATUS', id: 'linked', showCol: false },
    { label: 'ID_RUST', id: 'macAddress', showCol: true },
    { label: 'RUSTDESK_VERSION', id: 'rustdesk_version', showCol: true },
    { label: 'CREATION_DATE', id: 'createdAt', showCol: false },
    { label: 'CUSTOMER', id: 'customer', showCol: false },
    { label: 'ORGANIZATION', id: 'organization', showCol: false },
    { label: 'BUILDING', id: 'building', showCol: false },
    { label: 'UNIT', id: 'unit', showCol: false },
    { label: 'ROOM', id: 'room', showCol: true },
  ];
  public tableData: ITableData[] = [];
  public tableConfigParams: ITableConfigParams = {
    id: "devices",
    referenceLabel: 'DEVICES',
    searchBy: ['label'],
    hideAddBtn: true,
    filters: [],
    defaultSort: 'label',
    serverSideFiltering: true
  };

  public currentPage: number = 1;
  public currentLimit: number = 30;
  public totalDevices: number = 0;
  public currentSearch: string = '';

  /**
   * Definisce l'altezza della form in numero di righe, di default 1, viene poi valorizzato correttamente nel costruttore
   */
  public formRows: number = 1;
  public admissionFormRows: number = 1;
  public medicalHistoryFormRows: number = 1;
  public isDataLoading: boolean = false;
  private autoReload = setInterval(() => {
    this.loadDevices(false, true);
  }, 15000)

  public editNameForm: ISimpleInput[] = [
    {
      id: 'firstDigit',
      type: 'input',
      length: 0,
      inputValue: '',
      label: 'INPUT_LABELS.NEW_DEVICE_NAME',
      inputRule: 'number',
      validators: [Validators.required]
    }, {
      id: 'secondDigit',
      type: 'input',
      length: 0,
      inputValue: '',
      label: 'INPUT_LABELS.NEW_DEVICE_NAME',
      inputRule: 'number',
      validators: [Validators.required]
    }, {
      id: 'thirdDigit',
      type: 'input',
      length: 0,
      inputValue: '',
      label: 'INPUT_LABELS.NEW_DEVICE_NAME',
      inputRule: 'number',
      validators: [Validators.required]
    }, {
      id: 'fourthDigit',
      type: 'input',
      length: 0,
      inputValue: '',
      label: 'INPUT_LABELS.NEW_DEVICE_NAME',
      inputRule: 'number',
      validators: [Validators.required]
    }
  ];

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public sensorService: SensorService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private alertController: AlertController,
    private dialog: MatDialog,
    private requestsService: RequestsService,
    private filtersList: FiltersList,
    private tableService: TableService,
  ) {
  }

  private destroyed$ = new Subject<void>();
  private searchInput$ = new Subject<string>();

  ngOnInit() {
    this.requestsService.getCurrentUser()!.role === 'superuser' && this.tableColumns.unshift({ label: 'ID', id: 'id', showCol: true });
    this.loadFilters();
    this.restoreTablePreferences();
    this.searchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroyed$)
    ).subscribe((searchValue) => {
      this.currentSearch = searchValue;
      this.currentPage = 1;
      this.loadDevices(false);
    });
    this.loadDevices();
  }

  loadFilters() {
    !this.tableConfigParams.filters?.length && (this.tableConfigParams.filters = [
      this.filtersList.getFilterById('status'),
      this.filtersList.getFilterById('facilities'),
      this.requestsService.getCurrentUser()!.role === 'superuser' && (this.filtersList.getFilterById('customer')),
      this.requestsService.getCurrentUser()!.role === 'superuser' && (this.filtersList.getFilterById('organization')),
      this.filtersList.getFilterById('building'),
      this.filtersList.getFilterById('unit'),
      this.filtersList.getFilterById('room'),
    ].filter((filter) => Boolean(filter)) as ITableConfigParamsFilter[])
  }

  private restoreTablePreferences() {
    const savedPreferences = this.tableService.getTablePreferences(this.tableConfigParams.id);

    if (!Array.isArray(savedPreferences?.filters)) {
      return;
    }

    savedPreferences.filters.forEach((savedFilter: IUserTableFilterPreference) => {
      const currentFilter = this.tableConfigParams.filters?.find((filter) => filter.id === savedFilter.id);

      if (currentFilter) {
        currentFilter.selectedValue = savedFilter.selectedValue ?? currentFilter.selectedValue;
      }
    });
  }

  private getFilterById(filterId: string): ITableConfigParamsFilter | undefined {
    return this.tableConfigParams.filters?.find((filter) => filter.id === filterId);
  }

  private getSingleFilterValue(filterId: string): string | undefined {
    const selectedValue = this.getFilterById(filterId)?.selectedValue;

    if (typeof selectedValue !== 'string' || selectedValue === '' || selectedValue === 'all') {
      return undefined;
    }

    return selectedValue;
  }

  private getMultiFilterValues(filterId: string): string[] {
    const selectedValue = this.getFilterById(filterId)?.selectedValue;

    if (Array.isArray(selectedValue)) {
      return selectedValue.filter((value): value is string => typeof value === 'string' && value !== '' && value !== 'all');
    }

    if (typeof selectedValue === 'string' && selectedValue !== '' && selectedValue !== 'all') {
      return selectedValue.split(',').filter(Boolean);
    }

    return [];
  }

  private mapAssociationFilterValue(): IGetDevicesParams['association'] | undefined {
    const associationValue = this.getSingleFilterValue('facilities');

    if (associationValue === 'linked') {
      return 'associated';
    }

    if (associationValue === 'notLinked') {
      return 'available';
    }

    return undefined;
  }

  private mapStatusFilterValues(): string[] {
    const statusFilter = this.getFilterById('status');
    const selectedStatuses = this.getMultiFilterValues('status');
    const availableStatuses = (statusFilter?.options || [])
      .filter((option) => option.id !== 'all')
      .map((option) => option.id);

    if (!selectedStatuses.length || (availableStatuses.length && selectedStatuses.length === availableStatuses.length)) {
      return [];
    }

    return [...new Set(
      selectedStatuses
        .map((status) => status === 'warning' ? 'error' : status)
        .filter((status) => ['online', 'offline', 'disabled', 'error'].includes(status))
    )];
  }

  private buildDevicesQueryParams(): IGetDevicesParams {
    const params: IGetDevicesParams = {
      includeTechnicalDetails: true,
      page: this.currentPage,
      limit: this.currentLimit
    };

    const trimmedSearch = this.currentSearch.trim();
    const mappedStatuses = this.mapStatusFilterValues();
    const association = this.mapAssociationFilterValue();

    trimmedSearch && (params.label = trimmedSearch);
    this.getSingleFilterValue('customer') && (params.customerId = this.getSingleFilterValue('customer'));
    this.getSingleFilterValue('organization') && (params.organizationId = this.getSingleFilterValue('organization'));
    this.getSingleFilterValue('building') && (params.buildingId = this.getSingleFilterValue('building'));
    this.getSingleFilterValue('unit') && (params.unitId = this.getSingleFilterValue('unit'));
    this.getSingleFilterValue('room') && (params.roomId = this.getSingleFilterValue('room'));
    association && (params.association = association);
    mappedStatuses.length && (params.status = mappedStatuses.join(','));

    return params;
  }

  /**
   * @description Funzione che va ad aggiornare i dati della table in pagina
   */
  loadDevices(refreshData: boolean = false, reloadOnlyOnStatusChange: boolean = false) {
    if (refreshData && this.isDataLoading) {
      return;
    }
    this.isDataLoading = !reloadOnlyOnStatusChange && true;
    this.sensorService.getDevices$(this.buildDevicesQueryParams()).pipe(
      take(1),
      tap((devicesData) => {
        this.totalDevices = devicesData.total;
      }),
      map((devicesData) => devicesData.devices),
      tap((devices) => {
        if (reloadOnlyOnStatusChange && this.tableData) {
          const devicesByStatus = this.tableData.reduce((acc, curr) => {
            const currentDevice = curr.data as IDevice & { stereoCalibrationState?: string | null };
            acc[currentDevice.id] = {
              status: currentDevice.status,
              stereoCalibrationState: currentDevice.stereoCalibrationState ?? null
            };
            return acc;
          }, {} as Record<string, { status: IDevice['status']; stereoCalibrationState: string | null }>);
          if (!devices.some((device) => {
            const previousDevice = devicesByStatus[device.id];
            return !previousDevice
              || previousDevice.status !== device.status
              || previousDevice.stereoCalibrationState !== (device.stereoCalibrationHealth?.state ?? null);
          })) {
            return;
          }
        }
        this.tableData = devices.map((device) => {
          const signalDbm = device.signal_level_dbm;
          let signalIcon = 'wifi_off';
          let signalColor = 'var(--ion-color-medium)'; // Grigio default per disconnesso

          if (signalDbm !== undefined && signalDbm !== null) {
            if (signalDbm >= -65) {
              signalIcon = 'wifi';
              signalColor = '#A5D6A7';
            } else if (signalDbm >= -75) {
              signalIcon = 'wifi';
              signalColor = '#FFE082';
            } else {
              signalIcon = 'wifi_1_bar';
              signalColor = '#EF9A9A';
            }
          }
          return {
            rowStyle: {
              disabledLine: device.status === 'disabled'
            },
            data: {
              ...device,
              stereoCalibrationState: device.stereoCalibrationHealth?.state ?? null,
              signalDbm: signalDbm,       // Lo salviamo per mostrarlo nel tooltip
              signalIcon: signalIcon,     // Icona scelta
              signalColor: signalColor,   // Colore scelto
              facilities: !!device.facilities?.room,
              room: device.facilities?.room?.name || null,
              building: device.facilities?.building?.name || null,
              unit: device.facilities?.unit?.name || null,
              activeAllerts: null,
              customer: device.facilities?.customer?.name || null,
              temperature_celsius: device.temperature_celsius?.toFixed(0) + ' C°',
              organization: device.facilities?.organization?.name || null,
              createdAt: moment(device.createdAt).format('DD/MM/YYYY'),
              updatedAt: moment(device.updatedAt).format('DD/MM/YYYY'),
              lastHeartbeat: device.lastHeartbeat ? moment(device.lastHeartbeat).format('DD/MM - HH:mm') : undefined,
              linked: !!device.facilities?.room ? this.translateService.instant('TABLE.FILTER.OPTIONS.LINKED') : this.translateService.instant('TABLE.FILTER.OPTIONS.NOT_LINKED')
            },
            options: [
              {
                action: 'delete',
                icon: 'delete',
                label: 'DELETE'
              },
              {
                action: 'edit',
                icon: 'edit',
                label: 'EDIT_SENSOR_NAME',
              },
              ...['online', 'warning', 'offline'].includes(device.status) ? [
                {
                  action: 'linkRoom',
                  icon: 'room_preferences',
                  label: device.facilities?.room ? 'EDIT_LINKED_ROOM' : 'LINK_ROOM'
                }, {
                  action: 'unlink',
                  icon: 'close',
                  label: 'UNLINK_DEVICE'
                }] : [],
              ...device.status === 'online' ? [{
                action: 'viewStream',
                icon: 'visibility',
                label: 'VIEW_STREAM'
              }, {
                action: 'rustdeskConnection',
                icon: 'cast',
                label: 'RUSTDESK_CONNECTION',
                disabled: true
              }, {
                action: 'showMentorageConfig',
                icon: 'description',
                label: 'MENTORAGE_CONFIG'
              }, {
                action: 'showLiveTelemetry',
                icon: 'live_tv',
                label: 'LIVE_TELEMETRY'
              }
              ] : []
            ].filter((x) => x),
          } as ITableData;
        });
        this.isDataLoading = false;
        refreshData && this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.DATA_REFRESHED'), IonicColors.GREEN);
      }),
      catchError(err => {
        this.isDataLoading = false;
        refreshData && this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.LOADING_DATA'), IonicColors.RED);
        throw err
      })
    ).subscribe()
  }

  /**
   * @description Funzione invocata al click di una opzione della table
   * @param event Viene passato un oggetto contenente l'id delle risorsa in oggetto e l'action invocata
   */
  async onOptionAction(event: IOptionOutput) {
    switch (event.action) {
      case 'linkRoom':
        if (!this.requestsService.hasLinkedOrg()) {
          const customerAlert = await this.alertController.create({
            mode: 'md',
            cssClass: 'soft-alert-dialog',
            header: this.translateService.instant('GENERAL.MESSAGES.CAUTION'),
            message: this.translateService.instant('GENERAL.MESSAGES.ORGANIZATION_REQUIRED_ON_DEVICE_LINK_MESSAGE'),
            buttons: [
              {
                text: this.translateService.instant('GENERAL.BUTTONS.SETTINGS'),
                cssClass: 'alert-btn-primary',
                handler: () => {
                  customerAlert.dismiss();
                  this.router.navigateByUrl('wita/settings');
                },
              },
              {
                text: this.translateService.instant('GENERAL.BUTTONS.CANCEL'),
                cssClass: 'alert-btn-neutral',
                handler: () => {
                  customerAlert.dismiss();
                },
              }
            ],
          });
          await customerAlert.present();
        } else {
          if (event.resource['room']) {
            this.router.navigate([`/wita/devices/link/${event.resource.id}`], { state: { additionalData: event.resource, breadCrumbSubject: event.resource['label'] } });
          } else {
            this.router.navigate([`/wita/devices/link`], { state: { additionalData: event.resource, breadCrumbSubject: event.resource['label'] } });
          }
        }
        break;
      case 'unlink':
        this.isDataLoading = true;
        this.sensorService.linkDevice$(event.resource.id).pipe(
          tap(() => {
            this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.UNLINKED_DEVICE'), IonicColors.GREEN);
            this.loadDevices(false);
          }),
          catchError((err) => {
            this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.UNLINKED_DEVICE'), IonicColors.RED);
            throw err;
          })
        ).subscribe()
        break;
      case 'viewStream':
        this.dialog.open(DialogComponent, {
          data: {
            title: `${this.translateService.instant('PAGES.DEVICES.STREAMING')} ${event.resource.label}`,
            mode: 'streaming',
            additionalData: {
              id: event.resource.id,
              name: event.resource.label,
              status: event.resource.status
            }
          } as IDialogData
        });
        break;
      case 'rustdeskConnection':
        this.dialog.open(DialogComponent, {
          data: {
            title: `${this.translateService.instant('PAGES.DEVICES.RUSTDESK_CONNECTION_TITLE')} ${event.resource.label}`,
            mode: 'rustdeskIframe',
            additionalData: {
              id: event.resource.id,
              name: event.resource.label,
              status: event.resource.status,
              rustdeskId: event.resource.macAddress
            }
          } as IDialogData,
          width: '80vw',
          height: '85vh',
          maxWidth: '95vw',
        });
        break;
      case 'showMentorageConfig':
        this.router.navigate(
          [`/wita/devices/mentorageConfig/${event.resource.id}`],
          {
            state: {
              additionalData: {
                id: event.resource.id,
                label: event.resource.label,
                macAddress: event.resource.macAddress
              },
              breadCrumbSubject: event.resource.label
            }
          }
        );
        break;
      case 'delete':
        const customerAlert = await this.alertController.create({
          mode: 'md',
          cssClass: 'soft-alert-dialog',
          header: this.translateService.instant('GENERAL.MESSAGES.CAUTION'),
          message: this.translateService.instant('GENERAL.MESSAGES.DELETE_DEVICE_MESSAGE'),
          buttons: [
            {
              text: this.translateService.instant('GENERAL.BUTTONS.OK'),
              cssClass: 'alert-btn-danger',
              handler: () => {
                customerAlert.dismiss();
                this.isDataLoading = true;
                this.sensorService.deleteDevice$(event.resource.label).pipe(
                  tap(() => {
                    this.loadDevices()
                  })
                ).subscribe();
              },
            },
            {
              text: this.translateService.instant('GENERAL.BUTTONS.CANCEL'),
              cssClass: 'alert-btn-neutral',
              handler: () => {
                customerAlert.dismiss();
              },
            }
          ],
        });
        await customerAlert.present();
        break;
      case 'edit':
        const currentValue = Array.from(event.resource.label.replace('MENT-', ''));
        this.editNameForm.forEach((input, idx) => {
          input.inputValue = currentValue[idx] as string;
        });
        const dialrogRefEdit = this.dialog.open(DialogComponent, {
          data: {
            formData: this.editNameForm,
            additionalData: {
              valuesToCheck: this.tableData.map((tableData) => (tableData.data as IDevice).label?.replace('MENT-', '')).filter((value) => value != event.resource.label.replace('MENT-', ''))
            },
            title: this.translateService.instant('PAGES.DEVICES.CHANGE_SENSOR_NAME'),
            mode: 'editDeviceName'
          } as IDialogData
        });
        dialrogRefEdit.afterClosed().subscribe((data: number[] | false) => {
          if (data) {
            this.isDataLoading = true;
            this.sensorService.editDeviceName$({
              oldLabel: 'MENT-' + currentValue.join(''),
              newLabel: 'MENT-' + data.join('')
            }).pipe(
              tap(() => {
                this.toastService.showToast(this.translateService.instant('GENERAL.SUCCESS.DEVICE_NETWORK_CREDENTIALS'), IonicColors.GREEN);
                this.loadDevices(false);
              }),
              catchError((err) => {
                this.toastService.showToast(this.translateService.instant('GENERAL.ERRORS.DEVICE_NETWORK_CREDENTIALS'), IonicColors.RED);
                throw err;
              })
            ).subscribe();
          }
        });
        break;
      case 'showLiveTelemetry':
        this.router.navigate([`/wita/devices/liveTelemetry/${event.resource.label}`], { state: { additionalData: event.resource } });
        break;
      default:
        this.router.navigate(['/wita/devices']);
        break;
    }
  }

  /**
   * @description Funzione invocata al click di un button (Aggiungi risorsa, refresh dati, ecc...)
   * @param event Viene passato un oggetto contenente il tipo di action invocata
   */
  onCtaAction(event: ICtaOutput) {
    switch (event.action) {
      case 'refresh':
        this.loadDevices(true);
        break;
      case 'add':
        this.router.navigate(['/wita/devices/new']);
        break;
      default:
        break;
    }
  }

  onFiltersChange() {
    this.currentPage = 1;
    this.loadDevices(false);
  }

  onSearchChange(searchValue: string | null) {
    this.searchInput$.next((searchValue || '').trim());
  }

  handlePagination(event: PageEvent) {
    // Angular Material Paginator usa un indice a base 0 (0, 1, 2...), 
    // mentre l'API solitamente vuole a base 1 (1, 2, 3...)
    this.currentPage = event.pageIndex + 1;
    this.currentLimit = event.pageSize;

    // Ricarichiamo i dati con i nuovi parametri
    this.loadDevices(false);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    clearInterval(this.autoReload)
  }
}
